import { API_DOMAIN, ORDERS_URL } from 'config/constants';
import { api } from 'lib/api';
import { checkStatus } from 'lib/helpers';
import { z } from 'zod';
import { zProductSection } from './sections';
import { zOrderUser } from './order';

export const zProductSale = z.object({
	_id: z.string(),
	productId: z.string(),
	orderId: z.string(),
	title: z.string(),
	code: z.string().optional(),
	price: z.number(),
	priceTotal: z.number(),
	quantity: z.number(),
	amountPaid: z.number().optional(),
	productAmountPaid: z.number().optional().nullable(),
	mixer: z.string(),
	vendor: z.string(),
	first_name: z.string(),
	last_name: z.string(),
	tableNumber: z.string(),
	createdAt: z.string(),
	confirmationDate: z.string().optional().nullable(),
	cancellationDate: z.string().optional().nullable(),
	reservationDate: z.string().optional().nullable(),
	uniqueKey: z.string(),
	status: z.string(),
	sectionData: zProductSection,
	orderCode: z.string().optional(),
	paymentMethod: z.string().optional(),
	isInAppSale: z.boolean().optional(),
	saleFee: z.number().optional(),
	saleDiscount: z.number().optional(),
	pointsUsed: z.number().optional(),
	orderTotal: z.number().optional(),
	orderBy: z
		.object({
			username: z.string(),
			email: z.string().optional(),
			first_name: z.string().optional(),
			last_name: z.string().optional(),
		})
		.optional(),
	orderTo: z
		.object({
			username: z.string(),
			email: z.string().optional(),
			first_name: z.string().optional(),
			last_name: z.string().optional(),
		})
		.optional(),
	categories: z.array(z.string()).optional(),
	// Consumed with an NFC card ("Tarjeta Mute"): not Mute's revenue
	isNfcCard: z.boolean().optional(),
	confirmedBy: z
		.object({
			username: z.string(),
			email: z.string().optional(),
			first_name: z.string().optional(),
			last_name: z.string().optional(),
		})
		.optional()
		.nullable(),
	cancelledBy: z
		.object({
			username: z.string(),
			email: z.string().optional(),
			first_name: z.string().optional(),
			last_name: z.string().optional(),
		})
		.optional()
		.nullable(),
});

export const zSoldProductByLocation = z.object({
	_id: z.object({
		productId: z.string(),
		mixer: z.string().nullable(),
	}),
	totalPriceSum: z.number(),
	totalQuantities: z.number(),
	nfcCardTotalPriceSum: z.number().optional(),
	nfcCardQuantities: z.number().optional(),
	comboTitle: z.string(),
	productId: z.string(),
	code: z.string().optional(),
	section: z.string().optional(),
	sectionData: zProductSection,
	orderId: z.string(),
});

export const zSoldProductByVendor = z.object({
	_id: z.object({
		vendor: z.string(),
		productId: z.string(),
		mixer: z.string().nullable(),
	}),
	totalPriceSum: z.number(),
	totalQuantities: z.number(),
	nfcCardTotalPriceSum: z.number().optional(),
	nfcCardQuantities: z.number().optional(),
	comboTitle: z.string(),
	productId: z.string(),
	code: z.string().optional(),
	vendor: z.string(),
	section: z.string().optional(),
	sectionData: zProductSection,
	orderId: z.string(),
});

export type DataSoldProducts = {
	total: number;
	elements: PopulatedSoldProducts[];
};

export type DataSoldProductsByVendor = {
	total: number;
	elements: PopulatedSoldProductsByVendor[];
};
export type DataSoldProductsByLocation = {
	total: number;
	elements: PopulatedSoldProductsByLocation[];
};
export type DataSoldOrders = {
	total: number;
	elements: PopulatedSoldOrder[];
};
export type SoldSummary = {
	totalOrders: number;
	totalProductsSold: number;
	totalPaid: number;
	totalSaleFee: number;
	totalSaleDiscount: number;
	totalDelivered: number;
	totalReturned: number;
	totalDeliveredProducts: number;
	totalReturnedProducts: number;
	// The part of each total above consumed with NFC cards ("Tarjeta Mute")
	nfcCardOrders: number;
	nfcCardProductsSold: number;
	nfcCardTotalPaid: number;
	nfcCardTotalSaleFee: number;
	nfcCardTotalSaleDiscount: number;
	nfcCardTotalDelivered: number;
	nfcCardTotalReturned: number;
	nfcCardDeliveredProducts: number;
	nfcCardReturnedProducts: number;
};

export type FetchSoldProductsSearchProps = {
	search?: string;
	from?: string;
	to?: string;
	confirmationFrom?: string;
	confirmationTo?: string;
	cancellationFrom?: string;
	cancellationTo?: string;
	reservationFrom?: string;
	reservationTo?: string;
	limit?: number;
	skip?: number;
	sort?: Record<string, 'asc' | 'desc'>;
	statuses?: string[];
	vendors?: string[];
	sections?: string[];
	isInAppSale?: boolean;
	inAppSaleType?: string;
	reservationType?: string;
};

export const zSoldOrder = z.object({
	_id: z.string(),
	createdAt: z.string(),
	confirmationDate: z.string().optional().nullable(),
	cancellationDate: z.string().optional().nullable(),
	orderBy: zOrderUser,
	orderTo: zOrderUser.optional(),
	confirmedBy: zOrderUser.optional().nullable(),
	cancelledBy: zOrderUser.optional().nullable(),
	code: z.string().optional(),
	total: z.number().optional(),
	saleFee: z.number().optional(),
	saleDiscount: z.number().optional(),
	amountPaid: z.number().optional(),
	pointsUsed: z.number().optional(),
	paymentMethod: z.string().optional(),
	sectionData: zProductSection.optional(),
	status: z.string(),
	// Consumed with an NFC card ("Tarjeta Mute"): not Mute's revenue
	isNfcCard: z.boolean().optional(),
});

export type PopulatedSoldOrder = z.infer<typeof zSoldOrder>;

export const zSoldSummary = z.object({
	totalOrders: z.number().default(0),
	totalProductsSold: z.number().default(0),
	totalPaid: z.number().default(0),
	totalSaleFee: z.number().default(0),
	totalSaleDiscount: z.number().default(0),
	totalDelivered: z.number().default(0),
	totalReturned: z.number().default(0),
	totalDeliveredProducts: z.number().default(0),
	totalReturnedProducts: z.number().default(0),
	nfcCardOrders: z.number().default(0),
	nfcCardProductsSold: z.number().default(0),
	nfcCardTotalPaid: z.number().default(0),
	nfcCardTotalSaleFee: z.number().default(0),
	nfcCardTotalSaleDiscount: z.number().default(0),
	nfcCardTotalDelivered: z.number().default(0),
	nfcCardTotalReturned: z.number().default(0),
	nfcCardDeliveredProducts: z.number().default(0),
	nfcCardReturnedProducts: z.number().default(0),
});

type FilterParams = {
	search?: string;
};

type OptionsParams = {
	limit?: number;
	skip?: number;
	sort?: Record<string, 'asc' | 'desc'>;
};

export async function ListSoldProducts(props?: FetchSoldProductsSearchProps): Promise<DataSoldProducts> {
	try {
		const params: Record<string, string> = {};

		// Add filter for search (must be JSON stringified)
		if (props?.search) {
			const filter: FilterParams = { search: props.search };
			params.filter = JSON.stringify(filter);
		}

		// Add date range filters (sent as direct query params)
		if (props?.from) {
			params.from = props.from;
		}
		if (props?.to) {
			params.to = props.to;
		}

		// Add confirmation date range filters
		if (props?.confirmationFrom) {
			params.confirmationFrom = props.confirmationFrom;
		}
		if (props?.confirmationTo) {
			params.confirmationTo = props.confirmationTo;
		}

		// Add cancellation date range filters
		if (props?.cancellationFrom) {
			params.cancellationFrom = props.cancellationFrom;
		}
		if (props?.cancellationTo) {
			params.cancellationTo = props.cancellationTo;
		}
		if (props?.reservationFrom) {
			params.reservationFrom = props.reservationFrom;
		}
		if (props?.reservationTo) {
			params.reservationTo = props.reservationTo;
		}

		// Add statuses filter (sent as direct query param, comma-separated)
		if (props?.statuses && props.statuses.length > 0) {
			params.statuses = props.statuses.join(',');
		}

		// Add vendors filter (sent as direct query param, comma-separated)
		if (props?.vendors && props.vendors.length > 0) {
			params.vendors = props.vendors.join(',');
		}

		// Add sections filter (sent as direct query param, comma-separated)
		if (props?.sections && props.sections.length > 0) {
			params.sections = props.sections.join(',');
		}
		if (props?.isInAppSale !== undefined) {
			params.isInAppSale = String(props.isInAppSale);
		}
		if (props?.isInAppSale !== undefined) {
			params.isInAppSale = String(props.isInAppSale);
		}
		if (props?.inAppSaleType) {
			params.inAppSaleType = props.inAppSaleType;
		}
		if (props?.reservationType) {
			params.reservationType = props.reservationType;
		}

		// Add pagination and sort options (must be JSON stringified)
		if (props?.limit !== undefined || props?.skip !== undefined || props?.sort) {
			const options: OptionsParams = {};
			if (props.limit !== undefined) {
				options.limit = props.limit;
			}
			if (props.skip !== undefined) {
				options.skip = props.skip;
			}
			if (props.sort) {
				options.sort = props.sort;
			}
			params.options = JSON.stringify(options);
		}

		const response = await api.get<DataSoldProducts>(`${API_DOMAIN}${ORDERS_URL}/list-sold-products`, {
			params,
		});

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		if (!Array.isArray(response.data.elements)) {
			throw new Error('invalid response');
		}
		return response.data;
	} catch (error) {
		console.error(error);
		return { total: 0, elements: [] };
	}
}

export async function ListSoldProductsByVendor(
	props?: FetchSoldProductsSearchProps
): Promise<DataSoldProductsByVendor> {
	try {
		const params: Record<string, string> = {};

		// Add filter for search (must be JSON stringified)
		if (props?.search) {
			const filter: FilterParams = { search: props.search };
			params.filter = JSON.stringify(filter);
		}

		// Add date range filters
		if (props?.from) {
			params.from = props.from;
		}
		if (props?.to) {
			params.to = props.to;
		}

		// Add confirmation date range filters
		if (props?.confirmationFrom) {
			params.confirmationFrom = props.confirmationFrom;
		}
		if (props?.confirmationTo) {
			params.confirmationTo = props.confirmationTo;
		}

		// Add cancellation date range filters
		if (props?.cancellationFrom) {
			params.cancellationFrom = props.cancellationFrom;
		}
		if (props?.cancellationTo) {
			params.cancellationTo = props.cancellationTo;
		}
		if (props?.reservationFrom) {
			params.reservationFrom = props.reservationFrom;
		}
		if (props?.reservationTo) {
			params.reservationTo = props.reservationTo;
		}

		// Add statuses filter
		if (props?.statuses && props.statuses.length > 0) {
			params.statuses = props.statuses.join(',');
		}

		// Add vendors filter
		if (props?.vendors && props.vendors.length > 0) {
			params.vendors = props.vendors.join(',');
		}

		// Add sections filter
		if (props?.sections && props.sections.length > 0) {
			params.sections = props.sections.join(',');
		}
		if (props?.isInAppSale !== undefined) {
			params.isInAppSale = String(props.isInAppSale);
		}
		if (props?.inAppSaleType) {
			params.inAppSaleType = props.inAppSaleType;
		}
		if (props?.reservationType) {
			params.reservationType = props.reservationType;
		}

		// Add pagination and sort options (must be JSON stringified)
		if (props?.limit !== undefined || props?.skip !== undefined || props?.sort) {
			const options: OptionsParams = {};
			if (props.limit !== undefined) {
				options.limit = props.limit;
			}
			if (props.skip !== undefined) {
				options.skip = props.skip;
			}
			if (props.sort) {
				options.sort = props.sort;
			}
			params.options = JSON.stringify(options);
		}

		const response = await api.get<DataSoldProductsByVendor>(
			`${API_DOMAIN}${ORDERS_URL}/list-sold-products-by-vendor`,
			{ params }
		);
		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		if (!Array.isArray(response.data.elements)) {
			throw new Error('invalid response');
		}
		return response.data;
	} catch (error) {
		console.error(error);
		return { total: 0, elements: [] };
	}
}

export async function ListSoldProductsByLocation(
	props?: FetchSoldProductsSearchProps
): Promise<DataSoldProductsByLocation> {
	try {
		const params: Record<string, string> = {};

		// Add filter for search (must be JSON stringified)
		if (props?.search) {
			const filter: FilterParams = { search: props.search };
			params.filter = JSON.stringify(filter);
		}

		// Add date range filters
		if (props?.from) {
			params.from = props.from;
		}
		if (props?.to) {
			params.to = props.to;
		}

		// Add confirmation date range filters
		if (props?.confirmationFrom) {
			params.confirmationFrom = props.confirmationFrom;
		}
		if (props?.confirmationTo) {
			params.confirmationTo = props.confirmationTo;
		}

		// Add cancellation date range filters
		if (props?.cancellationFrom) {
			params.cancellationFrom = props.cancellationFrom;
		}
		if (props?.cancellationTo) {
			params.cancellationTo = props.cancellationTo;
		}
		if (props?.reservationFrom) {
			params.reservationFrom = props.reservationFrom;
		}
		if (props?.reservationTo) {
			params.reservationTo = props.reservationTo;
		}

		// Add statuses filter
		if (props?.statuses && props.statuses.length > 0) {
			params.statuses = props.statuses.join(',');
		}

		// Add vendors filter
		if (props?.vendors && props.vendors.length > 0) {
			params.vendors = props.vendors.join(',');
		}

		// Add sections filter
		if (props?.sections && props.sections.length > 0) {
			params.sections = props.sections.join(',');
		}
		if (props?.isInAppSale !== undefined) {
			params.isInAppSale = String(props.isInAppSale);
		}
		if (props?.inAppSaleType) {
			params.inAppSaleType = props.inAppSaleType;
		}
		if (props?.reservationType) {
			params.reservationType = props.reservationType;
		}

		// Add pagination and sort options (must be JSON stringified)
		if (props?.limit !== undefined || props?.skip !== undefined || props?.sort) {
			const options: OptionsParams = {};
			if (props.limit !== undefined) {
				options.limit = props.limit;
			}
			if (props.skip !== undefined) {
				options.skip = props.skip;
			}
			if (props.sort) {
				options.sort = props.sort;
			}
			params.options = JSON.stringify(options);
		}

		const response = await api.get<DataSoldProductsByLocation>(
			`${API_DOMAIN}${ORDERS_URL}/list-sold-products-by-location`,
			{ params }
		);

		if (!checkStatus(response)) throw new Error('invalid credentials');
		if (!Array.isArray(response.data.elements)) throw new Error('invalid response');

		return response.data;
	} catch (error) {
		console.error(error);
		return { total: 0, elements: [] };
	}
}

export async function ListSoldOrders(props?: FetchSoldProductsSearchProps): Promise<DataSoldOrders> {
	try {
		const params: Record<string, string> = {};

		if (props?.search) {
			const filter: FilterParams = { search: props.search };
			params.filter = JSON.stringify(filter);
		}

		if (props?.from) {
			params.from = props.from;
		}
		if (props?.to) {
			params.to = props.to;
		}

		if (props?.confirmationFrom) {
			params.confirmationFrom = props.confirmationFrom;
		}
		if (props?.confirmationTo) {
			params.confirmationTo = props.confirmationTo;
		}

		if (props?.cancellationFrom) {
			params.cancellationFrom = props.cancellationFrom;
		}
		if (props?.cancellationTo) {
			params.cancellationTo = props.cancellationTo;
		}
		if (props?.reservationFrom) {
			params.reservationFrom = props.reservationFrom;
		}
		if (props?.reservationTo) {
			params.reservationTo = props.reservationTo;
		}

		if (props?.statuses && props.statuses.length > 0) {
			params.statuses = props.statuses.join(',');
		}

		if (props?.vendors && props.vendors.length > 0) {
			params.vendors = props.vendors.join(',');
		}

		if (props?.sections && props.sections.length > 0) {
			params.sections = props.sections.join(',');
		}
		if (props?.isInAppSale !== undefined) {
			params.isInAppSale = String(props.isInAppSale);
		}
		if (props?.inAppSaleType) {
			params.inAppSaleType = props.inAppSaleType;
		}
		if (props?.reservationType) {
			params.reservationType = props.reservationType;
		}

		if (props?.limit !== undefined || props?.skip !== undefined || props?.sort) {
			const options: OptionsParams = {};
			if (props.limit !== undefined) {
				options.limit = props.limit;
			}
			if (props.skip !== undefined) {
				options.skip = props.skip;
			}
			if (props.sort) {
				options.sort = props.sort;
			}
			params.options = JSON.stringify(options);
		}

		const response = await api.get<DataSoldOrders>(`${API_DOMAIN}${ORDERS_URL}/list-sold-orders`, {
			params,
		});

		if (!checkStatus(response)) throw new Error('invalid credentials');
		if (!Array.isArray(response.data.elements)) throw new Error('invalid response');

		return response.data;
	} catch (error) {
		console.error(error);
		return { total: 0, elements: [] };
	}
}

export const zSoldProductByEvent = z.object({
	eventId: z.string().nullable(),
	eventTitle: z.string(),
	eventDate: z.string().nullable(),
	eventOpening: z.string().nullable(),
	totalOrders: z.number(),
	totalProductsSold: z.number(),
	totalPaid: z.number(),
	totalSaleFee: z.number(),
	totalSaleDiscount: z.number(),
	paymentMethodBreakdown: z.object({
		wallet: z.number(),
		userBalance: z.number(),
		mercadopago: z.number(),
	}),
	// The part of each total above consumed with NFC cards ("Tarjeta Mute")
	nfcCardOrders: z.number().optional(),
	nfcCardProductsSold: z.number().optional(),
	nfcCardTotalPaid: z.number().optional(),
	nfcCardTotalSaleFee: z.number().optional(),
	nfcCardTotalSaleDiscount: z.number().optional(),
});

export type SoldProductByEvent = z.infer<typeof zSoldProductByEvent>;

export async function ListSoldProductsByEvent(
	props?: FetchSoldProductsSearchProps
): Promise<SoldProductByEvent[]> {
	try {
		const params: Record<string, string> = {};

		if (props?.from) {
			params.from = props.from;
		}
		if (props?.to) {
			params.to = props.to;
		}
		if (props?.confirmationFrom) {
			params.confirmationFrom = props.confirmationFrom;
		}
		if (props?.confirmationTo) {
			params.confirmationTo = props.confirmationTo;
		}
		if (props?.cancellationFrom) {
			params.cancellationFrom = props.cancellationFrom;
		}
		if (props?.cancellationTo) {
			params.cancellationTo = props.cancellationTo;
		}
		if (props?.statuses && props.statuses.length > 0) {
			params.statuses = props.statuses.join(',');
		}
		if (props?.vendors && props.vendors.length > 0) {
			params.vendors = props.vendors.join(',');
		}
		if (props?.sections && props.sections.length > 0) {
			params.sections = props.sections.join(',');
		}

		const response = await api.get<SoldProductByEvent[]>(
			`${API_DOMAIN}${ORDERS_URL}/list-sold-products-by-event`,
			{ params }
		);

		if (!checkStatus(response)) throw new Error('invalid credentials');
		if (!Array.isArray(response.data)) throw new Error('invalid response');

		return response.data;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export type SalesByDate = {
	date: string;
	totalPaid: number;
	totalOrders: number;
	// The part of each total above consumed with NFC cards ("Tarjeta Mute")
	nfcCardTotalPaid?: number;
	nfcCardOrders?: number;
};

export async function ListSalesByDate(props?: FetchSoldProductsSearchProps): Promise<SalesByDate[]> {
	try {
		const params: Record<string, string> = {};
		if (props?.from) params.from = props.from;
		if (props?.to) params.to = props.to;
		if (props?.confirmationFrom) params.confirmationFrom = props.confirmationFrom;
		if (props?.confirmationTo) params.confirmationTo = props.confirmationTo;
		if (props?.statuses && props.statuses.length > 0) {
			params.statuses = props.statuses.join(',');
		}
		if (props?.isInAppSale !== undefined) params.isInAppSale = String(props.isInAppSale);
		if (props?.inAppSaleType) params.inAppSaleType = props.inAppSaleType;

		const response = await api.get<SalesByDate[]>(
			`${API_DOMAIN}${ORDERS_URL}/list-sales-by-date`,
			{params},
		);

		if (!checkStatus(response)) throw new Error('invalid credentials');
		if (!Array.isArray(response.data)) throw new Error('invalid response');

		return response.data;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export async function ListSoldSummary(props?: FetchSoldProductsSearchProps): Promise<SoldSummary> {
	try {
		const params: Record<string, string> = {};

		if (props?.search) {
			const filter: FilterParams = { search: props.search };
			params.filter = JSON.stringify(filter);
		}

		if (props?.from) {
			params.from = props.from;
		}
		if (props?.to) {
			params.to = props.to;
		}

		if (props?.confirmationFrom) {
			params.confirmationFrom = props.confirmationFrom;
		}
		if (props?.confirmationTo) {
			params.confirmationTo = props.confirmationTo;
		}

		if (props?.cancellationFrom) {
			params.cancellationFrom = props.cancellationFrom;
		}
		if (props?.cancellationTo) {
			params.cancellationTo = props.cancellationTo;
		}
		if (props?.reservationFrom) {
			params.reservationFrom = props.reservationFrom;
		}
		if (props?.reservationTo) {
			params.reservationTo = props.reservationTo;
		}

		if (props?.statuses && props.statuses.length > 0) {
			params.statuses = props.statuses.join(',');
		}

		if (props?.vendors && props.vendors.length > 0) {
			params.vendors = props.vendors.join(',');
		}

		if (props?.sections && props.sections.length > 0) {
			params.sections = props.sections.join(',');
		}
		if (props?.isInAppSale !== undefined) {
			params.isInAppSale = String(props.isInAppSale);
		}
		if (props?.inAppSaleType) {
			params.inAppSaleType = props.inAppSaleType;
		}
		if (props?.reservationType) {
			params.reservationType = props.reservationType;
		}

		const response = await api.get<SoldSummary>(`${API_DOMAIN}${ORDERS_URL}/list-sold-summary`, {
			params,
		});

		if (!checkStatus(response)) throw new Error('invalid credentials');

		return zSoldSummary.parse(response.data);
	} catch (error) {
		console.error(error);
		return {
			totalOrders: 0,
			totalProductsSold: 0,
			totalPaid: 0,
			totalSaleFee: 0,
			totalSaleDiscount: 0,
			totalDelivered: 0,
			totalReturned: 0,
			totalDeliveredProducts: 0,
			totalReturnedProducts: 0,
			nfcCardOrders: 0,
			nfcCardProductsSold: 0,
			nfcCardTotalPaid: 0,
			nfcCardTotalSaleFee: 0,
			nfcCardTotalSaleDiscount: 0,
			nfcCardTotalDelivered: 0,
			nfcCardTotalReturned: 0,
			nfcCardDeliveredProducts: 0,
			nfcCardReturnedProducts: 0,
		};
	}
}
